// An Angular auxiliary outlet segment, e.g. the "(m:class-detail/11692)" in
// /home(m:class-detail/11692). This portal declares no such outlet, so the
// router cannot match it on initial navigation and renders nothing.
const AUXILIARY_OUTLET = /\([^)]*:[^)]*\)/;

let capturedDeepLink = '';

// Capture the full original URL (path + query + fragment) before the Angular
// router runs. When the path contains an auxiliary outlet, normalise the bar to
// '/' so initial navigation matches the '**' route and renders the form; the
// original link is still preserved in memory for the redirect.
export function captureDeepLink(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const { pathname, search, hash } = window.location;
  capturedDeepLink = pathname && pathname !== '/' ? pathname + search + hash : '';

  if (AUXILIARY_OUTLET.test(pathname)) {
    history.replaceState(history.state, '', '/');
  }
}

export function getDeepLink(): string {
  return capturedDeepLink;
}

// Appends params to a URL while keeping any #fragment last, so query params are
// never swallowed into the fragment. The base is treated as opaque (its path,
// auxiliary outlet, and existing query are left untouched and never re-encoded);
// only the param values supplied by the caller are encoded by the caller.
export function buildUrlWithParams(
  base: string,
  params: Record<string, string | null | undefined>,
): string {
  const hashIndex = base.indexOf('#');
  let head = hashIndex === -1 ? base : base.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? '' : base.slice(hashIndex);

  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') {
      continue;
    }
    const separator = head.includes('?') ? '&' : '?';
    head += `${separator}${key}=${value}`;
  }

  return head + fragment;
}

// Builds a destination URL of the form
//   {portalUrl}/?redirectTo=<encoded deep link>&<extra params>
// The deep link goes INSIDE redirectTo so the destination portal's auth
// guard doesn't swallow our auxiliary params into its own redirectTo
// wrapper. When deepLinkPath is empty, redirectTo is omitted.
export function buildPortalUrl(
  portalUrl: string,
  deepLinkPath: string,
  params: Record<string, string | null | undefined>,
): string {
  const base = portalUrl.endsWith('/') ? portalUrl : portalUrl + '/';
  return buildUrlWithParams(base, {
    redirectTo: deepLinkPath ? encodeURIComponent(deepLinkPath) : '',
    ...params,
  });
}
